import { updateTaskPrice } from "apis/stateUpdate";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import {
  useGenerateBooksInvoiceMutation,
  useUpdateStateMutation,
  useUpdateTaskTaxAmoutMutation,
  useUploadCompletedTaskFileWithTypeMutation,
} from "store/apis/task";
import {
  QUARTERLY_ADVANCE_TAX,
  STATE_FRANCHISE_TAX_DELAWARE,
  US_BOOKKEEPING_ONE_TIME,
  US_BOOKKEEPING_SUBSCRIPTION_SUB_TASK,
} from "types/Models/services";
import { Task, TaskCompleteFileMetadata } from "types/Models/task";
import { BookkeepingFormValues, BookkeepingModal } from "./BookkeepingModal";
import { TaskCompleteModal } from "./TaskCompleteModal";

export type FileUploadValues = {
  files: TaskDocument[];
  amount: string;
  notes: string;
};

type TaskDocument = {
  docType: string;
  file: any;
  uuid: string | null;
};

type UpdateTaskPricePayload = {
  amount?: string;
  notes?: string;
  files?: TaskDocument[];
};

type UpdateTaskPriceProps = {
  show: boolean;
  closeModal: () => void;
  newStateId: string;
  groupId: string;
  taskId: string;
  task: Task;
  setShow: (show: boolean) => void;
  isVariablePricing: boolean;
  entityId: string;
};

export const UpdateTaskPrice = ({
  show,
  closeModal,
  newStateId,
  groupId,
  taskId,
  task,
  setShow,
  isVariablePricing,
  entityId,
}: UpdateTaskPriceProps) => {
  const { alertToast, successToast } = useToast();
  const [isBtnLoading, setBtnLoading] = useState<boolean>(false);
  const [updateTaskTaxAmout] = useUpdateTaskTaxAmoutMutation();
  const [updateState, { isLoading: isUpdatingState }] =
    useUpdateStateMutation();
  const [generateBooksInvoice, { isLoading: isGeneratingBooksInvoice }] =
    useGenerateBooksInvoiceMutation();
  const [uploadCompletedDoc, { isLoading: isUpdatingTaxAmount }] =
    useUploadCompletedTaskFileWithTypeMutation();

  const isDftTask: boolean =
    task.base_task_key === STATE_FRANCHISE_TAX_DELAWARE;
  const isAdvanceTax: boolean = task.base_task_key === QUARTERLY_ADVANCE_TAX;

  const isBookkeepingTask: boolean =
    task.base_task_key === US_BOOKKEEPING_ONE_TIME ||
    US_BOOKKEEPING_SUBSCRIPTION_SUB_TASK === task.base_task_key;

  const updatePrice = async (payload: UpdateTaskPricePayload) => {
    try {
      await updateTaskPrice({ taskId, payload, groupId });
      goToNextStep();
    } catch (error: any) {
      alertToast({ message: error.data?.error?.message });
    } finally {
      closeModal();
    }
  };

  const uploadTaskCompletedFile = async (values: FileUploadValues) => {
    try {
      const files: Record<string, any> = {};

      const filesMetadata: TaskCompleteFileMetadata[] = values.files
        .filter(({ file, docType }) => file && docType)
        .map(({ file, docType }, index) => {
          files[`file${index + 1}`] = file;
          return {
            key: `file${index + 1}`,
            file_id: file.uuid,
            document_type: docType as TaskCompleteFileMetadata["document_type"],
          };
        });

      if (filesMetadata.length > 0) {
        const payload = {
          files_metadata: filesMetadata,
          ...files,
        };

        await uploadCompletedDoc({
          task_id: taskId,
          group_id: groupId,
          payload,
        }).unwrap();
      }

      if (isDftTask || isAdvanceTax) {
        updateDFTPricing(values);
      } else {
        updatePrice(values);
      }
    } catch (e: any) {
      alertToast({ message: e?.data?.error?.message });
    }
    setShow(true);
  };

  const goToNextStep = async (): Promise<void> => {
    try {
      setBtnLoading(true);
      await updateState({ taskId, newStateId }).unwrap();
      successToast({ message: "Task completed successfully" });
      window.location.reload();
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    } finally {
      setBtnLoading(false);
      closeModal();
    }
  };

  const updateDFTPricing = async (values: FileUploadValues) => {
    try {
      await updateTaskTaxAmout({
        taskId,
        entityId,
        tax_amount_paid: values.amount,
      }).unwrap();
      goToNextStep();
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  const handleBookkeepingSubmit = async (values: BookkeepingFormValues) => {
    try {
      await generateBooksInvoice({
        entityId,
        taskId,
        payload: {
          accounting_method: values.accounting_method,
          from_date: values.from_date,
          to_date: values.to_date,
          no_of_transactions: values.no_of_transactions,
          cash_expense: values.cash_expense,
          intra_group_transfers: values.intra_group_transfers,
          intra_company_transfers: values.intra_company_transfers,
          monthly_billable_expenses_volume:
            values.monthly_billable_expenses_volume,
        },
      }).unwrap();
      // Use the new updateState mutation instead of stateUpdate
      await updateState({
        taskId,
        newStateId,
      }).unwrap();
      closeModal();
      successToast({ message: "Bookkeeping task completed successfully" });
      window.location.reload();
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  if (isBookkeepingTask) {
    return (
      <BookkeepingModal
        show={show}
        onClose={closeModal}
        onSubmit={handleBookkeepingSubmit}
        isSubmitting={isGeneratingBooksInvoice || isUpdatingState}
        task={task}
      />
    );
  }

  return (
    <TaskCompleteModal
      show={show}
      onClose={closeModal}
      isUpdatingTaxAmount={isUpdatingTaxAmount || isBtnLoading}
      uploadTaskCompletedFile={uploadTaskCompletedFile}
      showNotes={isVariablePricing}
      showAmount={isDftTask || isAdvanceTax || isVariablePricing}
      completedDocuments={task?.task_completed_documents || []}
      amount={task?.payment_amount || ""}
      groupId={groupId}
    />
  );
};
